import { IPage, IPageRange } from '@aon/aon-tws-common-ui';
import { cloneDeep as _cloneDeep } from 'lodash';
import { push } from 'react-router-redux';
import { Reducer } from 'redux';
import { ToastType } from '../components/audience/ToastType';
import { AudienceMode } from '../components/audiences/AudienceMode';
import Constants from '../constants';
import {
	ActionTypes,
	IAudienceCopyFailed,
	IAudienceCopyModal,
	IAudienceCopyRequested,
	IAudienceCopySucceeded,
	IAudienceDeleteAudienceWarningModal,
	IAudienceDeleteFailed,
	IAudienceDeleteRequested,
	IAudienceDeleteSucceeded,
	IAudienceListGetFailed,
	IAudienceListGetRequested,
	IAudienceListGetSucceeded,
	IAudiencePagedListGetSucceeded,
	IAudienceSendEmailModal,
	IUpdateAudienceColumns,
} from '../containers/audiences/AudienceListActions';
import ContentConstants from '../contentConstants';
import { IColumn } from '../models';
import { IAudience } from '../models/IAudience';
import * as AudienceAPI from '../services/AudienceAPI';
import { ColumnHelper } from '../utilities';
import { DistributionHelper } from '../utilities/DistributionHelper';
import { NavigationHelper } from '../utilities/NavigationHelper';
import { IApplicationState } from './';
import { ActionTypes as SharedActionTypes } from './ActionTypes';
import { resetAttributes } from './BranchingLogicAttributesSlice';
import { ActionCreators as SurveyAdminActionCreators } from './SurveyAdminStore';

export enum AudienceColumns {
	audienceName = 'audienceName',
	openOn = 'openOn',
	closeOn = 'closeOn',
	distributionType = 'distributionType',
	employeesCount = 'employeesCount',
	reminderEmailsCount = 'reminderEmailsCount',
	actions = 'actions',
	copy = 'copy',
}

export const defaultAudienceColumns: IColumn[] = [
	{ name: AudienceColumns.audienceName, displayText: 'Name', disabled: true, displayOrder: 0, checked: true },
	{ name: AudienceColumns.openOn, displayText: 'Open Date', disabled: false, displayOrder: 1, checked: true },
	{ name: AudienceColumns.closeOn, displayText: 'Close Date', disabled: false, displayOrder: 2, checked: true },
	{ name: AudienceColumns.distributionType, displayText: 'Distribution', disabled: false, displayOrder: 3, checked: true },
	{ name: AudienceColumns.employeesCount, displayText: 'Employees', disabled: false, displayOrder: 4, checked: true },
	{ name: AudienceColumns.reminderEmailsCount, displayText: 'Reminders', disabled: false, displayOrder: 5, checked: false },
	{ name: AudienceColumns.actions, displayText: 'Actions', disabled: true, displayOrder: 6, checked: true },
];

export interface IAudienceListState {
	audiences: IAudience[];
	pageRange: IPageRange;
	loading: boolean;
	columns: IColumn[];
	warningModalOpen: boolean;
	copyModalOpen: boolean;
	pendingDeleteAudience?: IAudience;
	pendingCopyAudience?: IAudience;
	pendingSendEmailAudience?: IAudience;
	sendEmailModalOpen: boolean;
	audienceDeleteInProgress?: boolean;
	audienceCopyInProgress?: boolean;
}

export const defaultState: IAudienceListState = {
	loading: false,
	audiences: [],
	pageRange: { offset: 0, limit: 25 },
	columns: [...defaultAudienceColumns],
	warningModalOpen: false,
	copyModalOpen: false,
	pendingDeleteAudience: undefined,
	pendingCopyAudience: undefined,
	pendingSendEmailAudience: undefined,
	sendEmailModalOpen: false,
};

type KnownActions =
	| IAudienceListGetRequested
	| IAudienceListGetSucceeded
	| IAudiencePagedListGetSucceeded
	| IAudienceListGetFailed
	| IAudienceDeleteRequested
	| IAudienceDeleteSucceeded
	| IAudienceCopyRequested
	| IAudienceCopySucceeded
	| IAudienceCopyFailed
	| IAudienceDeleteAudienceWarningModal
	| IAudienceSendEmailModal
	| IAudienceCopyModal
	| IAudienceDeleteFailed
	| IUpdateAudienceColumns;

export const ActionCreators = {
	GoToAudience: (route: string) => (dispatch: any) => {
		dispatch(push(route));
	},
	GetPagedAudiences: (surveyId: number, pageRange: IPageRange) => (dispatch: any) => {
		dispatch({ type: ActionTypes.AUDIENCE_LIST_GET_REQUESTED });

		return AudienceAPI.getPagedAudiences(surveyId, pageRange)
			.then((pagedResult: IPage<IAudience>) => {
				dispatch({ type: SharedActionTypes.TOAST_DISMISS_ALL });
				dispatch({
					type: ActionTypes.AUDIENCE_PAGED_LIST_GET_SUCCEEDED,
					payload: { audiences: pagedResult.items, pageRange: pagedResult.pageRange },
				});
			})
			.catch(() => {
				dispatch({ type: ActionTypes.AUDIENCE_LIST_GET_FAILED });
			});
	},
	CopyAudience:
		(audience: IAudience, isTestAudience: boolean, mode: AudienceMode) => (dispatch: any, getState: () => IApplicationState) => {
			dispatch({ type: ActionTypes.AUDIENCE_COPY_REQUESTED });
			dispatch({ type: SharedActionTypes.TOAST_PROCESSING_NOTIFICATION, payload: ContentConstants.TOAST_PROCESSING_TEXT });

			return AudienceAPI.copyAudience(audience.audienceId, isTestAudience, audience.surveyId)
				.then((copiedAudience: IAudience) => {
					dispatch({ type: ActionTypes.AUDIENCE_COPY_SUCCEEDED, payload: {} });
					dispatch({ type: SharedActionTypes.TOAST_SUCCESS_NOTIFICATION, payload: ContentConstants.TOAST_AUDIENCE_COPIED });
					if (mode === AudienceMode.Admin) {
						dispatch(SurveyAdminActionCreators.GetAudiences(audience.surveyId));
					} else {
						dispatch(ActionCreators.GetPagedAudiences(audience.surveyId, getState().audienceList.pageRange));
					}

					if (DistributionHelper.usesDistributionList(copiedAudience.distributionType) && mode !== AudienceMode.Admin) {
						dispatch(NavigationHelper.GoToAudienceDistributionOptions(copiedAudience.audienceId, copiedAudience.surveyId, mode));
					}
				})
				.catch(() => {
					dispatch({ type: ActionTypes.AUDIENCE_COPY_FAILED });
					dispatch({ type: SharedActionTypes.TOAST_WARNING_NOTIFICATION, payload: ContentConstants.TOAST_AUDIENCE_COPY_FAILED });
				});
		},
	DeleteAudience: (audience: IAudience) => (dispatch: any, getState: () => IApplicationState) => {
		dispatch({ type: ActionTypes.AUDIENCE_DELETE_REQUESTED });
		dispatch({ type: SharedActionTypes.TOAST_PROCESSING_NOTIFICATION, payload: ContentConstants.TOAST_PROCESSING_TEXT });

		return AudienceAPI.deleteAudience(audience, true)
			.then(() => {
				dispatch({ type: ActionTypes.AUDIENCE_DELETE_SUCCEEDED, payload: {} });
				dispatch({ type: SharedActionTypes.TOAST_SUCCESS_NOTIFICATION, payload: ContentConstants.TOAST_AUDIENCE_DELETED });
				dispatch(ActionCreators.GetPagedAudiences(audience.surveyId, getState().audienceList.pageRange));
				dispatch(resetAttributes());
			})
			.catch(() => {
				dispatch({ type: ActionTypes.AUDIENCE_DELETE_FAILED });
				dispatch({ type: SharedActionTypes.TOAST_WARNING_NOTIFICATION, payload: ContentConstants.TOAST_AUDIENCE_DELETED_FAILED });
			});
	},
	AddNewAudience: () => (dispatch: any) => {
		dispatch({ type: ActionTypes.AUDIENCE_SET_DEFAULT_STATE });
		dispatch(push(Constants.ROUTE_AUDIENCE));
	},
	UpdateColumnSelections: (values: string[]) => (dispatch: any) => {
		dispatch({ type: ActionTypes.AUDIENCES_UPDATE_COLUMNS, payload: values });
	},
	ToggleWarningModal: (open: boolean, audience?: IAudience) => (dispatch: any) => {
		dispatch({ type: ActionTypes.AUDIENCES_DELETE_AUDIENCE_WARNING_MODAL_SUCCEEDED, payload: { open, audience } });
	},
	ToggleSendEmailModal: (open: boolean, audience?: IAudience) => (dispatch: any) => {
		dispatch({ type: ActionTypes.AUDIENCES_SEND_EMAIL_MODAL_SUCCEEDED, payload: { open, audience } });
	},
	ToggleCopyAudienceModal: (open: boolean, audience?: IAudience) => (dispatch: any) => {
		dispatch({ type: ActionTypes.AUDIENCES_COPY_AUDIENCE_MODAL_SUCCEEDED, payload: { open, audience } });
	},
	Toast: (type: ToastType, message: string) => (dispatch: any) => {
		if (type === ToastType.Warning) {
			dispatch({ type: SharedActionTypes.TOAST_WARNING_NOTIFICATION, payload: message });
		} else if (type === ToastType.Success) {
			dispatch({ type: SharedActionTypes.TOAST_SUCCESS_NOTIFICATION, payload: message });
		} else {
			dispatch({ type: SharedActionTypes.TOAST_INFO_NOTIFICATION, payload: message });
		}
	},
};

export const reducer: Reducer<IAudienceListState, KnownActions> = (state: IAudienceListState = defaultState, action: KnownActions) => {
	switch (action.type) {
		case ActionTypes.AUDIENCE_LIST_GET_REQUESTED:
			return { ...state, loading: true };

		case ActionTypes.AUDIENCE_LIST_GET_SUCCEEDED:
			return {
				...state,
				audiences: action.payload.audiences,
				columns: [...defaultAudienceColumns],
				loading: false,
			};
		case ActionTypes.AUDIENCE_PAGED_LIST_GET_SUCCEEDED:
			return {
				...state,
				audiences: action.payload.audiences,
				pageRange: action.payload.pageRange,
				columns: [...defaultAudienceColumns],
				loading: false,
			};
		case ActionTypes.AUDIENCE_LIST_GET_FAILED:
			return { ...state, loading: false };
		case ActionTypes.AUDIENCE_COPY_REQUESTED:
			return { ...state, loading: false, copyModalOpen: false, audienceCopyInProgress: true };
		case ActionTypes.AUDIENCE_COPY_SUCCEEDED:
			return { ...state, loading: false, pendingCopyAudience: undefined, audienceCopyInProgress: false };
		case ActionTypes.AUDIENCE_COPY_FAILED:
			return { ...state, loading: false, audienceCopyInProgress: false };
		case ActionTypes.AUDIENCE_DELETE_REQUESTED:
			return { ...state, loading: false, warningModalOpen: false, audienceDeleteInProgress: true };
		case ActionTypes.AUDIENCE_DELETE_SUCCEEDED:
			return { ...state, loading: false, pendingDeleteAudience: undefined, audienceDeleteInProgress: false };
		case ActionTypes.AUDIENCE_DELETE_FAILED:
			return { ...state, loading: false, audienceDeleteInProgress: false };
		case ActionTypes.AUDIENCES_DELETE_AUDIENCE_WARNING_MODAL_SUCCEEDED:
			return { ...state, warningModalOpen: action.payload.open, pendingDeleteAudience: action.payload.audience };
		case ActionTypes.AUDIENCES_SEND_EMAIL_MODAL_SUCCEEDED:
			return { ...state, sendEmailModalOpen: action.payload.open, pendingSendEmailAudience: action.payload.audience };
		case ActionTypes.AUDIENCES_COPY_AUDIENCE_MODAL_SUCCEEDED:
			return { ...state, copyModalOpen: action.payload.open, pendingCopyAudience: action.payload.audience };
		case ActionTypes.AUDIENCES_UPDATE_COLUMNS: {
			const copy = _cloneDeep(state.columns);
			const updatedColumns = ColumnHelper.UpdateSelectedColumns(copy, action.payload);
			return { ...state, columns: updatedColumns };
		}
		default:
			return state;
	}
};
